/**
 * Optimistic quick edits on one item (a tick, a chip select, a star, a reorder): shown at once,
 * sent, and either confirmed by the server's copy or put back with a toast.
 *
 * Two rules keep overlapping edits honest:
 *  - while several are in flight, only the last response is applied, so an earlier answer
 *    landing late can't briefly undo a later edit;
 *  - a failure reverts only what that edit changed, and only where the item still shows the
 *    edit's value (`revertFields`), so it never undoes another edit made meanwhile.
 */
import { useRef } from 'react';

/**
 * Puts back the fields an edit changed — but only those still showing the edit's value, so a
 * later edit to the same field isn't undone by an earlier one failing.
 */
export function revertFields<T extends object>(
  current: T,
  sent: Partial<T>,
  before: Partial<T>,
): T {
  const next = { ...current };
  for (const key of Object.keys(sent) as (keyof T)[])
    if (current[key] === sent[key]) next[key] = before[key] as T[keyof T];
  return next;
}

/** The named fields of `source`: the "before" half of `revertFields`. */
export const pickFields = <T extends object>(source: T, keys: readonly (keyof T)[]): Partial<T> =>
  Object.fromEntries(keys.map((key) => [key, source[key]])) as Partial<T>;

export interface QuickEditOptions<T> {
  /** Changes the cached item in place. */
  edit: (map: (current: T) => T) => void;
  /** Replaces the cached item with the server's copy. */
  confirm: (saved: T) => void;
  /** Reports a refused edit (its revert has already run). */
  fail: (error: unknown) => void;
}

/**
 * Returns `quick(apply, revert, send)` for one item. Mount it where the item is (the item's
 * dialog, keyed by its id), so the in-flight count is that item's alone.
 */
export function useQuickEdit<T>({ edit, confirm, fail }: QuickEditOptions<T>) {
  const working = useRef(0);
  return async (
    apply: (current: T) => T,
    revert: (current: T) => T,
    send: () => Promise<T | void>,
  ): Promise<void> => {
    edit(apply);
    working.current += 1;
    let saved: T | void;
    try {
      saved = await send();
    } catch (error) {
      working.current -= 1;
      edit(revert);
      return fail(error);
    }
    // Outside the try: a confirm that throws is a bug to surface, not a refused edit to revert.
    working.current -= 1;
    if (working.current === 0 && saved) confirm(saved);
  };
}
